import { z } from 'zod';
import { DbJsonSchema } from '../../../dbJsonSchemas.js';
import { AutomatedEmailTypeEnum, ClaimWindowStartEvent, CrewClaimResolutionMethodEnum, CrewIncentiveTypeEnum, EspMetric, FinalizationEmailTypeEnum, GiftCardType, InspectionGradeCode, ShipmentTrackingEventProvider, StoreInventoryBehavior, } from '../../../enums/crew.js';
import { ShippingApiProvider } from '../../../enums/enum.js';
import { ClaimReasonEnum, FulfillmentStrategyEnum, ShopifyAppSubscriptionStatusEnum, } from '../../../enums/gsp.js';
import { enumToZodLiteralUnion, helperSchema } from '../../../zodExtensions.js';
import { Carrier, CarrierService } from './storeShipRate.js';
export const settingsTheme = DbJsonSchema.StoreUiConfig.customerUiConfigSchema;
/** Related to database schema where columns have been set to `@db.Decimal(6, 2)`, as values must round to be less than 10k */
const lessThan10k = helperSchema.nonNegativeNumber.lt(10_000, {
    message: 'Must be less than 10,000',
});
/** A stored credential, returned masked (`null` when unset) and never in full. */
const maskedSecret = z.string().nullable();
/** A secret as a form submits it — blank when the merchant is not changing it. */
const submittedSecret = z.string().trim();
/**
 * What a merchant can do to a credential-bearing integration, as separate union
 * members: `configure` has no secret field to accidentally drop, and
 * `connect`/`rotateSecret` cannot be satisfied by an empty string. Integrations
 * that support it add `disconnectAction` alongside these.
 */
const credentialActions = (config, secrets) => ({
    connect: z.object({
        action: z.literal('connect'),
        ...config,
        ...secrets,
    }),
    rotateSecret: z.object({
        action: z.literal('rotateSecret'),
        ...config,
        ...secrets,
    }),
    configure: z.object({ action: z.literal('configure'), ...config }),
});
const disconnectAction = z.object({ action: z.literal('disconnect') });
const baseEmailTemplate = z.object({
    id: z.number(),
    name: z.string(), // display name - non-editable
    sendAfterDays: z.number().nullable(),
    header: helperSchema.nonEmptyString,
    subject: helperSchema.nonEmptyString,
    body: helperSchema.nonEmptyString,
});
const automatedEmailTemplate = baseEmailTemplate.merge(z.object({
    isEnabled: z.boolean(),
    type: enumToZodLiteralUnion(AutomatedEmailTypeEnum),
}));
const finalizeEmailTemplates = baseEmailTemplate.merge(z.object({
    type: enumToZodLiteralUnion(FinalizationEmailTypeEnum),
}));
const notifications = z.object({
    isNewClaimEmailEnabled: z.boolean(),
    emailsForNewClaim: helperSchema.nullifiedEmailList,
    isClaimReceivedEmailEnabled: z.boolean(),
    emailsForClaimReceived: helperSchema.nullifiedEmailList,
    emailsForFinalizationFailure: helperSchema.nullifiedEmailList,
    isFailureToFinalizeEmailEnabled: z.boolean(),
});
const email = z
    .object({
    emailSendFrom: helperSchema.nullifiedEmail,
    emailSendFromName: helperSchema.nullifiedString,
    emailReplyTo: helperSchema.nullifiedEmail,
    emailReplyToName: helperSchema.nullifiedString,
    verifiedEmailDomain: z.string().nullable(),
    isAutoFinalizeEmailEnabled: z.boolean(),
    storeEmailTemplates: z.object({
        automatedEmailTemplates: z.array(automatedEmailTemplate),
        finalizeEmailTemplates: z.array(finalizeEmailTemplates),
    }),
})
    .superRefine(({ verifiedEmailDomain, emailSendFrom }, ctx) => {
    if (!emailSendFrom)
        return;
    const isValidVerifiedEmail = z
        .email()
        .endsWith(`@${verifiedEmailDomain}`)
        .or(z.literal('noreply@corso.com'))
        .safeParse(emailSendFrom).success;
    if (!isValidVerifiedEmail) {
        ctx.addIssue({
            code: 'custom',
            path: ['emailSendFrom'],
            message: `Email send from must be a valid email address ending with ${verifiedEmailDomain}.`,
        });
    }
});
export const appSubscription = z.object({
    appSubscriptionStatus: enumToZodLiteralUnion(ShopifyAppSubscriptionStatusEnum),
    appSubscriptionConfirmationUrl: z.string(),
    isActive: z.boolean(), // * is the store active (payment method configured, able to create claims)
});
// -------------- klaviyo ----------------
const klaviyoDisconnected = z.object({
    startOauthUrl: z.string(),
    connected: z.literal(false),
});
const klaviyoConnected = z.object({
    disconnectOauthUrl: z.string(),
    isEnabled: z.boolean(),
    isOrderTrackingEnabled: z.boolean(),
    connected: z.literal(true),
});
const klaviyoConfig = z.discriminatedUnion('connected', [
    klaviyoDisconnected,
    klaviyoConnected,
]);
// --------------- stripe ----------------
const stripeDisconnected = z.object({
    startOauthUrl: z.string(),
    connected: z.literal(false),
});
const stripeConnected = z.object({
    idFromPlatform: z.string(),
    connected: z.literal(true),
});
const stripeConfig = z.discriminatedUnion('connected', [
    stripeDisconnected,
    stripeConnected,
]);
// --------------- shipBob ----------------
const shipBobConfig = z.object({
    id: z.number(),
    shouldCreateRma: z.boolean(),
    maskedAccessToken: maskedSecret,
});
const shipBobActions = credentialActions({ shouldCreateRma: z.boolean() }, { accessToken: helperSchema.nonEmptyString });
const shipBobConfigUpdate = z.discriminatedUnion('action', [
    shipBobActions.connect,
    shipBobActions.rotateSecret,
    shipBobActions.configure,
    disconnectAction,
]);
export const shipBobConfigForm = shipBobActions.connect
    .omit({ action: true })
    .extend({ accessToken: submittedSecret });
// --------------- shipHero ----------------
const shipHeroConfig = z.object({
    id: z.number(),
    shouldCreateRma: z.boolean(),
    maskedRefreshToken: maskedSecret,
});
const shipHeroActions = credentialActions({ shouldCreateRma: z.boolean() }, { refreshToken: helperSchema.nonEmptyString });
const shipHeroConfigUpdate = z.discriminatedUnion('action', [
    shipHeroActions.connect,
    shipHeroActions.rotateSecret,
    shipHeroActions.configure,
    disconnectAction,
]);
export const shipHeroConfigForm = shipHeroActions.connect
    .omit({ action: true })
    .extend({ refreshToken: submittedSecret });
// --------------- blueBox ----------------
const blueBoxConfig = z.object({
    id: z.number(),
    username: z.string(),
    maskedPassword: maskedSecret,
    shouldCreateRma: z.boolean(),
});
const blueBoxActions = credentialActions({
    username: helperSchema.nonEmptyString,
    shouldCreateRma: z.boolean(),
}, { password: helperSchema.nonEmptyString });
const blueBoxConfigUpdate = z.discriminatedUnion('action', [
    blueBoxActions.connect,
    blueBoxActions.rotateSecret,
    blueBoxActions.configure,
    disconnectAction,
]);
export const blueBoxConfigForm = blueBoxActions.connect
    .omit({ action: true })
    .extend({ password: submittedSecret });
// --------------- masonHub ----------------
const masonHubConfig = z.object({
    id: z.number(),
    urlSlug: z.string(),
    maskedApiToken: maskedSecret,
    shouldCreateRma: z.boolean(),
});
const masonHubActions = credentialActions({
    urlSlug: helperSchema.nonEmptyString,
    shouldCreateRma: z.boolean(),
}, { apiToken: helperSchema.nonEmptyString });
const masonHubConfigUpdate = z.discriminatedUnion('action', [
    masonHubActions.connect,
    masonHubActions.rotateSecret,
    masonHubActions.configure,
    disconnectAction,
]);
export const masonHubConfigForm = masonHubActions.connect
    .omit({ action: true })
    .extend({ apiToken: submittedSecret });
// --------------- xbFulfillment ----------------
const xbFulfillmentConfig = z.object({
    id: z.number(),
    username: z.string(),
    maskedPassword: maskedSecret,
    endpoint: z.string(),
    shouldCreateRma: z.boolean(),
});
const xbFulfillmentActions = credentialActions({
    username: z.string(),
    endpoint: z.string(),
    shouldCreateRma: z.boolean(),
}, { password: helperSchema.nonEmptyString });
const xbFulfillmentConfigUpdate = z.discriminatedUnion('action', [
    xbFulfillmentActions.connect,
    xbFulfillmentActions.rotateSecret,
    xbFulfillmentActions.configure,
    disconnectAction,
]);
export const xbFulfillmentConfigForm = xbFulfillmentActions.connect
    .omit({ action: true })
    .extend({ password: submittedSecret });
// -------------- sendlane ----------------
const sendLaneConfig = z.object({
    id: z.number(),
    maskedAccessToken: maskedSecret,
});
// the token is Sendlane's only field, so there is nothing to `configure`
const sendLaneActions = credentialActions({}, { accessToken: helperSchema.nonEmptyString });
const sendLaneConfigUpdate = z.discriminatedUnion('action', [
    sendLaneActions.connect,
    sendLaneActions.rotateSecret,
    disconnectAction,
]);
export const sendLaneConfigForm = sendLaneActions.connect.omit({
    action: true,
});
// --------------- Gorgias ----------------
const gorgiasConfig = z.object({
    account: z.string(),
    scopes: z.string(),
    appUrl: z.string(),
    updateRequired: z.boolean(),
    teams: z.array(z.object({ id: z.number(), name: z.string() })).optional(),
    connected: z.literal(true),
});
const gorgiasConfigDisconnected = z.object({
    appUrl: z.string(),
    connected: z.literal(false),
});
const gorgiasConfigUnion = z.discriminatedUnion('connected', [
    gorgiasConfig,
    gorgiasConfigDisconnected,
]);
// -------------- Kustomer -------------------
const kustomerConfig = z.object({
    id: z.number(),
    maskedApiKey: maskedSecret,
    appIdVersion: z.string().nullable(),
    isEnabled: z.boolean(),
    triggerResync: z.boolean().optional(),
    appSettings: z.array(z.object({
        label: z.string(),
        key: z.union([
            z.literal('storeId'),
            z.literal('configId'),
            z.literal('token'),
        ]),
        value: z.string(),
    })),
});
// -------------- Zendesk -------------------
const zendeskConfig = z.object({
    id: z.number(),
    subdomain: z.string(),
    installationId: z.string(),
});
// --------------- shipping provider ----------------
const carrier = z.object({
    id: z.number(),
    idFromPlatform: z.string(),
    name: z.string(),
    ccsDetail: z
        .object({
        carrierCode: z.enum(Carrier),
        services: z.array(z.enum(CarrierService)),
    })
        .optional(),
});
const shippingProvider = z.object({
    provider: z.union([
        z.literal(ShippingApiProvider.easyPost),
        z.literal(ShippingApiProvider.vesyl),
    ]),
    maskedApiKey: maskedSecret,
    carriers: z.array(carrier),
    enabledCarriers: z.array(carrier),
    enabledCarriersMerchant: z.array(carrier),
    isEnabled: z.boolean(),
    isCarrierCalculatedServiceEnabled: z.boolean(),
});
const statusConfig = z.object({
    id: z.number(),
    startReturnUrl: z.string().optional(),
    idFromPlatform: z.string(),
});
const rechargeConfig = z.object({
    id: z.number(),
    maskedApiKey: maskedSecret,
    isEnabled: z.boolean(),
});
const rechargeActions = credentialActions({ isEnabled: z.boolean() }, { apiKey: helperSchema.nonEmptyString });
const skioConfig = z.object({
    id: z.number(),
    maskedAccessToken: maskedSecret,
    webhookUrl: z.string(),
    isEnabled: z.boolean(),
    // Skio generates this when the webhook URL is registered, so it round-trips
    webhookToken: z.string(),
});
const skioActions = credentialActions({ isEnabled: z.boolean(), webhookToken: z.string() }, { accessToken: helperSchema.nonEmptyString });
const skioUpdate = z.discriminatedUnion('action', [
    skioActions.connect,
    skioActions.rotateSecret,
    skioActions.configure,
]);
export const skioConfigForm = skioActions.connect
    .omit({ action: true })
    .extend({ accessToken: submittedSecret });
const gladlyConfig = z.object({
    id: z.number(),
});
const localizeConfig = z.object({ id: z.number() });
const brijConfig = z.object({
    id: z.number(),
    startRegistrationUrl: z.string(),
});
const zowieConfig = z.object({ id: z.number() });
const globalEConfig = z.object({
    id: z.number(),
    isEnabled: z.boolean(),
    merchantGuid: z.string(),
    replacementOrderShippingTitle: helperSchema.optionalizedString,
});
const logiwaConfig = z.object({
    id: z.number(),
    shouldCreateRma: z.boolean(),
});
const happyReturnsConfig = z.object({
    id: z.number(),
    retailerId: z.string(),
    isEnabled: z.boolean(),
});
const twoBoxesConfig = z.object({
    id: z.number(),
    isEnabled: z.boolean(),
    corsoWebhookUrl: z.string(),
    maskedWebhookToken: maskedSecret,
    // a string in and a string out, so react-hook-form can infer the field type
    webhookId: z
        .string()
        .transform((val) => {
        const trimmed = val.trim();
        const matches = trimmed.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi);
        // If the string is already a UUID or contains one, return the last match.
        return matches?.[matches.length - 1] ?? trimmed;
    })
        .pipe(z.string().uuid({
        message: 'Please provide a valid webhook ID or a URL containing one (e.g., 123e4567-e89b-12d3-a456-426614174000)',
    })),
    maskedHmacSignatureSecret: maskedSecret,
});
const twoBoxesActions = credentialActions({
    isEnabled: z.boolean(),
    webhookId: twoBoxesConfig.shape.webhookId,
}, {
    webhookToken: helperSchema.nonEmptyString,
    hmacSignatureSecret: helperSchema.nonEmptyString,
});
// Two Boxes is the only integration with two secrets, so a rotation takes either
// one on its own rather than forcing the merchant to re-paste both
const twoBoxesRotate = twoBoxesActions.rotateSecret
    .extend({
    webhookToken: helperSchema.nonEmptyString.optional(),
    hmacSignatureSecret: helperSchema.nonEmptyString.optional(),
})
    .refine(({ webhookToken, hmacSignatureSecret }) => Boolean(webhookToken) || Boolean(hmacSignatureSecret), { message: 'Provide a new webhook token or HMAC signature secret.' });
export const twoBoxesConfigForm = twoBoxesActions.connect
    .omit({ action: true })
    .extend({
    webhookToken: submittedSecret,
    hmacSignatureSecret: submittedSecret,
});
const integrations = z.object({
    appSubscription,
    stripeConfig,
    klaviyoConfig,
    happyReturnsConfig: happyReturnsConfig.nullable(),
    twoBoxesConfig: twoBoxesConfig.nullable(),
    logiwaConfig: logiwaConfig.nullable(),
    kustomerConfig: kustomerConfig.nullable(),
    zendeskConfig: zendeskConfig.nullable(),
    vesylConfig: shippingProvider.nullable(),
    rechargeConfig: rechargeConfig.nullable(),
    shipHeroConfig: shipHeroConfig.nullable(),
    gorgiasConfig: gorgiasConfigUnion,
    shipBobConfig: shipBobConfig.nullable(),
    statusConfig: statusConfig.nullable(),
    gladlyConfig: gladlyConfig.nullable(),
    easyPostConfig: shippingProvider.nullable(),
    blueBoxConfig: blueBoxConfig.nullable(),
    masonHubConfig: masonHubConfig.nullable(),
    sendLaneConfig: sendLaneConfig.nullable(),
    zowieConfig: zowieConfig.nullable(),
    globalEConfig: globalEConfig.nullable(),
    brijConfig: brijConfig.nullable(),
    localizeConfig: localizeConfig.nullable(),
    xbFulfillmentConfig: xbFulfillmentConfig.nullable(),
    skioConfig: skioConfig.nullable(),
});
export const statusConfigUpdate = z.object({
    statusConfig: statusConfig
        .omit({
        id: true,
        startReturnUrl: true,
    })
        .merge(z.object({
        disconnect: z.boolean().optional(),
    })),
    kind: z.literal('statusConfig'),
});
const shippingProviderActions = credentialActions({
    provider: shippingProvider.shape.provider,
    isEnabled: z.boolean(),
    isCarrierCalculatedServiceEnabled: z.boolean(),
    enabledCarriers: z.array(carrier.omit({ ccsDetail: true })),
    enabledCarriersMerchant: z.array(carrier.omit({ ccsDetail: true })),
}, { apiKey: helperSchema.nonEmptyString });
export const shippingProviderUpdate = z.discriminatedUnion('action', [
    shippingProviderActions.connect,
    shippingProviderActions.rotateSecret,
    shippingProviderActions.configure,
]);
export const shippingProviderForm = shippingProviderActions.connect
    .omit({ action: true })
    .extend({ apiKey: submittedSecret });
export const rechargeUpdate = z.object({
    kind: z.literal('rechargeConfig'),
    rechargeConfig: z.discriminatedUnion('action', [
        rechargeActions.connect,
        rechargeActions.rotateSecret,
        rechargeActions.configure,
    ]),
});
export const rechargeConfigForm = rechargeActions.connect
    .omit({ action: true })
    .extend({ apiKey: submittedSecret });
export const vesylUpdate = z.object({
    kind: z.literal('vesylConfig'),
    vesylConfig: shippingProviderUpdate,
});
export const easyPostUpdate = z.object({
    kind: z.literal('easyPostConfig'),
    easyPostConfig: shippingProviderUpdate,
});
export const shippingProviderRefresh = z.object({
    provider: z.union([
        z.literal(ShippingApiProvider.easyPost),
        z.literal(ShippingApiProvider.vesyl),
    ]),
});
export const shipHeroUpdate = z.object({
    shipHeroConfig: shipHeroConfigUpdate,
    kind: z.literal('shipHeroConfig'),
});
export const shipBobUpdate = z.object({
    shipBobConfig: shipBobConfigUpdate,
    kind: z.literal('shipBobConfig'),
});
export const blueBoxUpdate = z.object({
    blueBoxConfig: blueBoxConfigUpdate,
    kind: z.literal('blueBoxConfig'),
});
export const masonHubUpdate = z.object({
    masonHubConfig: masonHubConfigUpdate,
    kind: z.literal('masonHubConfig'),
});
export const sendlaneUpdate = z.object({
    sendLaneConfig: sendLaneConfigUpdate,
    kind: z.literal('sendLaneConfig'),
});
export const klaviyoUpdate = z.object({
    klaviyoConfig: klaviyoConnected.omit({
        connected: true,
        disconnectOauthUrl: true,
    }),
    kind: z.literal('klaviyoConfig'),
});
export const klaviyoTestEventRequest = z.object({
    metrics: z.array(enumToZodLiteralUnion(EspMetric)).min(1),
    claimExternalId: z.string().trim().optional(),
});
export const klaviyoTestEventResult = z.object({
    metric: enumToZodLiteralUnion(EspMetric),
    status: z.enum(['sent', 'skipped', 'failed']),
    detail: z.string().optional(),
});
export const klaviyoTestEventResponse = z.array(klaviyoTestEventResult);
export const xbFulfillmentUpdate = z.object({
    xbFulfillmentConfig: xbFulfillmentConfigUpdate,
    kind: z.literal('xbFulfillmentConfig'),
});
export const skioConfigUpdate = z.object({
    skioConfig: skioUpdate,
    kind: z.literal('skioConfig'),
});
const kustomerActions = credentialActions({
    isEnabled: z.boolean(),
    triggerResync: z.boolean().optional(),
}, { apiKey: helperSchema.nonEmptyString });
export const kustomerConfigUpdate = z.object({
    kind: z.literal('kustomerConfig'),
    kustomerConfig: z.discriminatedUnion('action', [
        kustomerActions.connect,
        kustomerActions.rotateSecret,
        kustomerActions.configure,
    ]),
});
export const kustomerConfigForm = kustomerActions.connect
    .omit({ action: true })
    .extend({ apiKey: submittedSecret });
export const zendeskConfigUpdate = z.object({
    kind: z.literal('zendeskConfig'),
    zendeskConfig: zendeskConfig.omit({ id: true }),
});
export const logiwaConfigUpdate = z.object({
    logiwaConfig: logiwaConfig.omit({ id: true }),
    kind: z.literal('logiwaConfig'),
});
// retailerId is provisioned during onboarding, merchants only toggle the integration
export const happyReturnsConfigUpdate = z.object({
    happyReturnsConfig: happyReturnsConfig.omit({ id: true, retailerId: true }),
    kind: z.literal('happyReturnsConfig'),
});
export const twoBoxesConfigUpdate = z.object({
    twoBoxesConfig: z.discriminatedUnion('action', [
        twoBoxesActions.connect,
        twoBoxesRotate,
        twoBoxesActions.configure,
    ]),
    kind: z.literal('twoBoxesConfig'),
});
export const globalEConfigUpdate = z.object({
    globalEConfig: globalEConfig
        .omit({ id: true })
        .extend({ merchantGuid: helperSchema.nonEmptyString }),
    kind: z.literal('globalEConfig'),
});
const integrationsUpdateDiscriminatedUnion = z.discriminatedUnion('kind', [
    shipHeroUpdate,
    twoBoxesConfigUpdate,
    vesylUpdate,
    easyPostUpdate,
    shipBobUpdate,
    statusConfigUpdate,
    rechargeUpdate,
    blueBoxUpdate,
    masonHubUpdate,
    sendlaneUpdate,
    klaviyoUpdate,
    xbFulfillmentUpdate,
    skioConfigUpdate,
    kustomerConfigUpdate,
    zendeskConfigUpdate,
    logiwaConfigUpdate,
    globalEConfigUpdate,
    happyReturnsConfigUpdate,
]);
// applies to all resolution methods
const returns = z.object({
    isReturnsEnabled: z.boolean(),
    isGiftReturnEnabled: z.boolean(),
    isReturnProcessApiEnabled: z.boolean(),
    allowClaimsOnPartiallyFulfilledOrders: z.boolean(),
    returnPolicyUrl: helperSchema.nullifiedUrl,
    isReturnInspectionEnabled: z.boolean(),
    claimWindowStartEvent: z.enum(ClaimWindowStartEvent),
    inventoryBehavior: z.enum(StoreInventoryBehavior),
    shouldConsolidateOrdersAtFinalization: z.boolean(),
    isClaimImageFromCustomerRequired: z.boolean(),
    exchangeOrderPrefix: helperSchema.nullifiedString,
    returnOrderPrefix: helperSchema.nullifiedString,
    replacementOrderDiscountCode: helperSchema.nullifiedString,
    exchangeOrderDiscountCode: helperSchema.nullifiedString,
    defaultReturnExpirationDays: helperSchema.policyDays,
});
const refunds = z.object({
    refundValidityDays: helperSchema.policyDays,
});
// displayed as store credit
const giftCards = z.intersection(z.object({
    giftCardValidityDays: helperSchema.policyDays,
    giftCardProductIdFromPlatform: helperSchema.nullifiedString,
    giftCardOrderPrefix: helperSchema.nullifiedString,
    giftCardType: z.enum(GiftCardType),
    giftCardAttributes: DbJsonSchema.StoreSettings.giftCardAttributes,
    giftCardExpirationDays: helperSchema.nonNegativeNumber.nullable(),
}), z.discriminatedUnion('giftCardIncentiveType', [
    z.object({
        giftCardIncentiveType: z.literal(CrewIncentiveTypeEnum.fixed),
        giftCardIncentiveAmount: lessThan10k,
    }),
    z.object({
        giftCardIncentiveType: z.literal(CrewIncentiveTypeEnum.percent),
        giftCardIncentiveAmount: z.number().min(0).max(100), // TODO make this a value between 0 and 1 with a maximum of 2 fractional digits to match the database schema
    }),
    z.object({
        giftCardIncentiveType: z.literal(CrewIncentiveTypeEnum.none),
        giftCardIncentiveAmount: helperSchema.nonNegativeNumber,
    }),
]));
const variantExchanges = z.object({
    variantExchangeValidityDays: helperSchema.policyDays,
    variantExchangeShippingTitle: helperSchema.nullifiedString,
    minInventoryAvailableToCustomer: helperSchema.nonNegativeNumber,
    /** @deprecated inert when `isClaimLineageEnabled`; remove once claim lineage is on for every store */
    isExchangeOfExchangeAllowed: z.boolean(),
    /** @deprecated inert when `isClaimLineageEnabled`; remove once claim lineage is on for every store */
    isExchangeOfExchangeCreditEnabled: z.boolean(),
    isClaimLineageEnabled: z.boolean(),
    isExchangeRecommendationEnabled: z.boolean(),
});
const registrations = z.object({
    // main setting control for registrations
    isRegistrationEnabled: z.boolean(),
    shouldCollectRegistrationEmailMarketingConsent: z.boolean(),
    emailMarketingConsentPolicyUrl: helperSchema.nullifiedUrl,
    useProductListPriceOnRegistration: z.boolean(),
    shouldCollectRegistrationProofOfPurchase: z.boolean(),
    shouldCollectRegistrationAddress: z.boolean(),
    isRegistrationWarrantyClaimEnabled: z.boolean(),
    isAutomatedRegistrationEnabled: z.boolean(),
    customFields: z.array(z.object({
        id: z.number(),
        name: z.string(),
        isRequired: z.boolean(),
    })),
    // sales channels where product registration is offered
    offeredSalesChannels: z.array(z.string()),
    // all possible sales channels
    salesChannelOptions: z.array(z.object({
        id: z.number(),
        name: z.string(),
    })),
});
const orderEditing = z.object({
    isOrderEditingEnabled: z.boolean(),
    orderEditingWindowMinutes: z.number().min(0),
    orderEditingWindowOverrides: DbJsonSchema.StoreSettings.orderEditingWindowOverrides.default([]),
    shouldHoldOrdersWithinEditWindow: z.boolean(),
    orderHoldReleaseTag: z.string().default('CORSO-OE-HOLD'),
    shouldUseCreditRefundForOrderEdits: z.boolean(),
    isRevertUnpaidEditsEnabled: z.boolean(),
});
const tracking = z.object({
    isTrackingEnabled: z.boolean(),
    trackingEventProvider: z.enum(ShipmentTrackingEventProvider),
});
const warranties = z.object({
    isWarrantiesEnabled: z.boolean(),
    warrantyPolicyUrl: helperSchema.nullifiedUrl,
    defaultWarrantyDays: helperSchema.policyDays,
    isWarrantyInspectionEnabled: z.boolean(),
    warrantyOrderPrefix: helperSchema.nullifiedString,
    isMultipleWarrantyClaimsAllowed: z.boolean(),
    defaultWarrantyRequestedResolutionMethod: z.enum(CrewClaimResolutionMethodEnum),
    warrantyGiftCardType: z.enum(GiftCardType),
});
const storeShipUnit = z.object({
    idFromPlatform: z.string(),
    rangeStart: z.number(),
    rangeEnd: z.number(),
    productVariant: z
        .object({
        price: z.string(),
        title: z.string(),
        options: z.array(z.object({ name: z.string(), value: z.string() })),
        imgUrl: z.string().nullable(),
        sku: z.string().nullable(),
        deletedOnPlatform: z.boolean().optional(),
    })
        .optional(),
    implementationType: z.union([
        z.enum(['Shipping_Line']),
        z.enum(['Product_Variant']),
    ]),
});
export const shippingProtection = z.object({
    isWidgetProtectionConfigured: z.boolean(),
    isZeroDollarReorderEnabled: z.boolean(),
    shipProtectOrderTag: z.string().nullable(),
    isCheckoutPlusEnabled: z.boolean(),
    isCheckoutPlusScriptConfigured: z.boolean(),
    // ? theme app embed
    activateCheckoutPlusEmbedUrl: z.string(),
    zeroDollarReorderDiscountTitle: z.string().nullable(),
    /**
     * @deprecated
     */
    checkoutPlusScriptUrl: z.string().nullable(),
    accessScopes: z.array(z.string()), // ? used to determine which scopes are enabled for the store
    // ? Shipping Plus Protection
    isShippingLineProtectionOffered: z.boolean(),
    isCarrierServiceConfigured: z.boolean(),
    isApplicationDiscountConfigured: z.boolean(),
    isRefundMultipleGspEnabled: z.boolean(),
    // ? related to both types of protection
    isClaimImageFromCustomerRequired: z.boolean(),
    fulfillmentStrategy: z.enum(FulfillmentStrategyEnum),
    shouldSendPurchaseConfirmation: z.boolean(),
    offeredClaimReasons: z.array(z.enum(ClaimReasonEnum)),
    giftCardProductIdFromPlatform: helperSchema.nullifiedString,
    shipProtectSet: z.object({
        units: z.array(storeShipUnit),
        name: z.string(),
        code: z.string(),
        maxProtectionValue: z.number(),
        minProtectionValue: z.number(),
        product: z.object({
            idFromPlatform: z.string(),
            title: z.string(),
            platformUrl: z.string(),
        }),
    }),
    widgetConfig: DbJsonSchema.StoreShipProtectSettings.widgetConfigSchema,
});
export const settingsConfig = z.object({
    email,
    returns,
    refunds,
    giftCards,
    variantExchanges,
    warranties,
    notifications,
    shippingProtection,
    registrations,
    orderEditing,
    tracking,
    inspectionGrades: z.array(z.object({
        id: z.number(),
        name: z.string(),
        code: z.enum(InspectionGradeCode),
    })),
});
export const settingsIntegration = integrations;
export const settingsIntegrationUpdate = integrationsUpdateDiscriminatedUnion;
export { shippingProvider };
//# sourceMappingURL=settings.js.map